// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, {useState, useEffect} from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { LoadingButton } from '@mui/lab';
import { CircularProgress, Grid, IconButton, Tooltip, Alert } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';

import {
    AwardsPayload, CertificationsPayload, DocumentType, DocumentTypes, AwardTemplateType
} from '../../utils/types';
import { CertificateService } from '../../services/certificateService';
import { pdf } from '@react-pdf/renderer';
import Award from './Award';
import QuarterlyAwards from './QuartelyAward';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, actions, ...other } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
      {...other}
    >
      <div>{children}</div>
      <div>
        {actions}
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </div>
    </DialogTitle>
  );
}

interface DocumentViewButtonProps {
  onClick: () => void;
}

function DocumentViewButton({ onClick }: DocumentViewButtonProps) {
  return (
    <Tooltip title="View and Download" placement="top" arrow>
      <IconButton onClick={onClick}>
        <VisibilityIcon />
      </IconButton>
    </Tooltip>
  );
}

interface DocumentViewDialogProps {
  document: any;
  documentType: DocumentType;
  onClose: () => void;
}

function DocumentViewDialog({ document, documentType, onClose }: DocumentViewDialogProps) {
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getTitle = () => {
    if (documentType === DocumentTypes.CERTIFICATE) {
      return document.displayName || document.name;
    } else {
      return `${document.name} ${document.quarter || ""} ${document.year}`;
    }
  };

  const getFilename = () => {
    if (documentType === DocumentTypes.CERTIFICATE) {
      return `${document.displayName || document.name}.pdf`;
    } else {
      return `${document.name} - ${document.year}.pdf`;
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      const a = window.document.createElement("a");
      a.href = pdfUrl;
      a.download = getFilename();
      a.click();
    }
  };

  useEffect(() => {
    const generatePdf = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let response;
        
        // Use appropriate service based on document type
        if (documentType === DocumentTypes.CERTIFICATE) {
          response = await CertificateService.generateCertificatePDF(document, documentType);
        } else if (documentType === AwardTemplateType.YEARLY || documentType === AwardTemplateType.QUARTERLY) {
          // Handle awards with frontend PDF generation
          try {
            let pdfComponent;
            if (documentType === AwardTemplateType.QUARTERLY) {
              pdfComponent = QuarterlyAwards({ certification: document as AwardsPayload });
            } else {
              pdfComponent = Award({ certification: document as AwardsPayload });
            }
            
            const pdfBlob = await pdf(pdfComponent).toBlob();
            response = {
              success: true,
              data: pdfBlob,
            };
          } catch (error) {
            response = {
              success: false,
              error: error instanceof Error ? error.message : 'Failed to generate award PDF',
            };
          }
        } else {
          setError('Unsupported document type');
          setLoading(false);
          return;
        }
        
        if (response.success && response.data) {
          const url = URL.createObjectURL(response.data);
          setPdfUrl(url);
        } else {
          setError(response.error || 'Failed to generate PDF');
        }
      } catch (err) {
        setError('Failed to generate PDF');
      } finally {
        setLoading(false);
      }
    };

    generatePdf();

    // Cleanup function to revoke URL when component unmounts
    return () => {
      if (pdfUrl) {
        if (documentType === DocumentTypes.CERTIFICATE) {
          CertificateService.revokePdfUrl(pdfUrl);
        } else {
          URL.revokeObjectURL(pdfUrl);
        }
      }
    };
  }, [document, documentType]);

  return (
    <BootstrapDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      fullWidth
      maxWidth="lg"
    >
      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={onClose}
        actions={
          loading ? (
            <LoadingButton translate="no" size="small" loading />
          ) : (
            <Tooltip title="Download" placement="top" arrow>
              <IconButton
                onClick={handleDownload}
                disabled={!pdfUrl}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )
        }
      >
        {getTitle()}
      </BootstrapDialogTitle>
      <DialogContent dividers>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "70vh" }}
        >
          {loading && <CircularProgress />}
          {error && (
            <Alert severity="error" sx={{ maxWidth: 400 }}>
              {error}
            </Alert>
          )}
          {!loading && !error && pdfUrl && (
            <iframe
              title="Certificate PDF Preview"
              style={{ width: "100%", height: "100%" }}
              src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=fit`}
            ></iframe>
          )}
        </Grid>
      </DialogContent>
    </BootstrapDialog>
  );
}

export interface DocumentViewProps {
  document: CertificationsPayload | AwardsPayload;
  documentType: DocumentType
}

export default function DocumentView({ document, documentType }: DocumentViewProps) {
  const [open, setOpen] = React.useState(false);

  if (!document) {
    return null;
  }

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <DocumentViewButton onClick={handleClickOpen} />
      {open && (
        <DocumentViewDialog 
          document={document} 
          documentType={documentType} 
          onClose={handleClose} 
        />
      )}
    </>
  );
}
